/**
 * diagnosticInterceptors — axios + window-level diagnostics layer.
 *
 * Operator brief (Feb 19, 2026):
 *   "Errors must be traceable. Users always get a calm recovery path."
 *
 * What this file installs:
 *   1. Axios response interceptor that classifies failures, generates
 *      a ref ID, fires a (rate-limited) POST to /api/debug/client-errors,
 *      and tags the error object so pages can read it back.
 *   2. window.onerror handler   → "unhandled_error" reports.
 *   3. unhandledrejection       → "unhandled_promise" reports.
 *
 * The interceptors NEVER swallow rejections — they only annotate.
 * 401 is left to authAxios.js (single-purpose). Reports to the
 * `/debug/client-errors` endpoint itself are skipped (no feedback
 * loop).
 */
import axios from "axios";
import {
  classifyError,
  generateRefId,
  reportClientError,
} from "./clientErrorReporter";


const REPORTER_HEADER = "x-ndg-internal";
const REPORTER_VALUE = "client-error-report";

// Categories that should NOT be reported automatically (handled by
// dedicated UI elsewhere, or noisy on flaky links).
const SKIP_AUTO_REPORT = new Set(["auth"]);


function _isReporterRequest(config) {
  const h = config?.headers;
  if (!h) return false;
  try {
    return h[REPORTER_HEADER] === REPORTER_VALUE
      || (typeof h.get === "function" && h.get(REPORTER_HEADER) === REPORTER_VALUE);
  } catch (_) {
    return false;
  }
}


export function installDiagnosticAxiosInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      try {
        if (_isReporterRequest(error?.config)) {
          // Never recurse — don't try to report a failure to report.
          return Promise.reject(error);
        }
        const category = classifyError(error);
        const refId = generateRefId();
        // Annotate so callers can render the ref ID alongside their
        // own UI without re-classifying.
        try {
          error.refId = refId;
          error.category = category;
        } catch (_) { /* sealed obj */ }

        if (!SKIP_AUTO_REPORT.has(category)) {
          // Fire-and-forget; never throws.
          reportClientError({
            refId,
            category,
            error,
            request: {
              url: error?.config?.url || null,
              method: error?.config?.method || null,
              status: error?.response?.status || null,
            },
          });
        }
      } catch (_) {
        // Interceptor must never break the rejection chain.
      }
      return Promise.reject(error);
    },
  );
}


export function installWindowDiagnosticHandlers() {
  if (typeof window === "undefined") return;

  // Guard against double-install in StrictMode dev double-mounts.
  if (window.__ndg_diag_window_installed__) return;
  window.__ndg_diag_window_installed__ = true;

  window.addEventListener("error", (e) => {
    try {
      const err = e?.error || new Error(e?.message || "Unhandled error");
      reportClientError({
        category: "unknown",
        error: err,
        extras: {
          source: "window.onerror",
          filename: e?.filename || null,
          lineno: e?.lineno || null,
          colno: e?.colno || null,
        },
      });
    } catch (_) { /* swallow */ }
  });

  window.addEventListener("unhandledrejection", (e) => {
    try {
      const reason = e?.reason;
      const err =
        reason instanceof Error
          ? reason
          : new Error(typeof reason === "string" ? reason : "Unhandled promise rejection");
      reportClientError({
        category: "unhandled_promise",
        error: err,
        extras: {
          source: "unhandledrejection",
          reason_kind: typeof reason,
        },
      });
    } catch (_) { /* swallow */ }
  });
}
